/**
 * EVENTSFLO-UI-HELP-BUTTON-STANDARD-V1
 *
 * Reusable Help button for any EventsFlo page/form. Renders a compact
 * `HelpCircle` icon button that opens a Shadcn Dialog explaining the
 * purpose of the page/form, sectioned usage guidance, and any
 * warnings/limitations.
 *
 * Props
 * ─────
 *   testId       string  Required. Stable id used for the test ids:
 *                          - page-help-button-{testId}
 *                          - page-help-dialog-{testId}
 *                          - page-help-title-{testId}
 *                          - page-help-section-{testId}-{i}
 *                          - page-help-warning-{testId}-{i}
 *   title        string  Dialog title. Defaults to "About this page".
 *   description  string  Short one-line description rendered under the title.
 *   sections     Array<{ heading: string, bullets: string[] }>
 *                  Optional list of grouped bullet sections.
 *   warnings     string[]
 *                  Optional list of limitation/warning lines rendered
 *                  in a distinct warning block.
 *   label        string  Optional button label next to the icon.
 *                          Defaults to "Help".
 *   variant      string  Button variant (default "outline").
 *   size         string  Button size (default "sm").
 *   className    string  Extra classes for the trigger button.
 */
import React, { useState } from "react";
import { HelpCircle, AlertTriangle } from "lucide-react";

import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

export function PageHelpButton({
  testId,
  title = "About this page",
  description = "",
  sections = [],
  warnings = [],
  label = "Help",
  variant = "outline",
  size = "sm",
  className = "",
}) {
  const [open, setOpen] = useState(false);

  if (!testId) {
    // testId is required so the page/form has a stable, unique handle
    // for tests. Fail loudly in dev rather than emit duplicate ids.
    console.warn("PageHelpButton: `testId` prop is required");
  }

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        className={`gap-1.5 ${className}`}
        onClick={() => setOpen(true)}
        aria-label={`${title} — open help`}
        data-testid={`page-help-button-${testId}`}
      >
        <HelpCircle className="h-4 w-4" />
        {label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-2xl max-h-[85vh] overflow-y-auto"
          data-testid={`page-help-dialog-${testId}`}
        >
          <DialogHeader>
            <DialogTitle
              className="flex items-center gap-2"
              data-testid={`page-help-title-${testId}`}
            >
              <HelpCircle className="h-5 w-5 text-blue-400" />
              {title}
            </DialogTitle>
            {description ? (
              <DialogDescription
                data-testid={`page-help-description-${testId}`}
              >
                {description}
              </DialogDescription>
            ) : null}
          </DialogHeader>

          <div className="space-y-5 mt-2">
            {sections.map((section, i) => (
              <section
                key={`${testId}-section-${i}`}
                data-testid={`page-help-section-${testId}-${i}`}
              >
                <h3
                  className="text-sm font-semibold text-white mb-1.5"
                  data-testid={`page-help-section-heading-${testId}-${i}`}
                >
                  {section.heading}
                </h3>
                <ul className="list-disc pl-5 space-y-1 text-sm text-zinc-300">
                  {(section.bullets || []).map((b, bi) => (
                    <li
                      key={`${testId}-section-${i}-b-${bi}`}
                      data-testid={`page-help-bullet-${testId}-${i}-${bi}`}
                    >
                      {b}
                    </li>
                  ))}
                </ul>
              </section>
            ))}

            {warnings && warnings.length > 0 ? (
              <section
                className="rounded border border-amber-500/40 bg-amber-500/10 p-3"
                data-testid={`page-help-warnings-${testId}`}
              >
                <h3 className="text-sm font-semibold text-amber-300 mb-1.5 flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4" />
                  Limitations &amp; rules
                </h3>
                <ul className="list-disc pl-5 space-y-1 text-sm text-amber-100/90">
                  {warnings.map((w, wi) => (
                    <li
                      key={`${testId}-warning-${wi}`}
                      data-testid={`page-help-warning-${testId}-${wi}`}
                    >
                      {w}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PageHelpButton;
